import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { getLocalizedValue, GlobalMessageUtil } from '@card-software/nexus-utilities';
import {
  ALL_POOL_NAMES,
  CostingPoolsProxyService,
  PoolName,
  PoolStatus,
  PoolStatusEntry,
} from '../../../../proxies/costing-pools.proxy.service';
import { ExchangeRateProxyService } from '../../../../proxies/exchange-rate.proxy.service';
import { CalculateMonthlyCostCommand, MonthlyCostProxyService, MonthlyCostRunStatus } from '../../../../proxies/monthly-cost.proxy.service';
import { Currency } from '../../../../models/exchange-rate/exchange-rate';

interface PoolRow {
  name: PoolName;
  status: PoolStatus | 'not-computed' | 'requesting';
  computedAt?: string | null;
  computedBy?: string | null;
  lastError?: string | null;
}

interface PoolsControlCompleted {
  /** True when at least one pool finished in <c>Ready</c> status this session. */
  anyReady: boolean;
  /** True when every pool is currently <c>Ready</c>. Drives the parent's
   *  decision on whether <c>/calculate</c> is now safe to run. */
  allReady: boolean;
}

/**
 * Pool Control Panel — drives /api/v1/costing-pools/{status,{poolName}/compute}.
 *
 * Sits next to Calculation Control. Lets the user (1) check current readiness
 * for the period's six costing pools, (2) compute or recompute each pool
 * individually (in case of a bad input it won't block the others), (3) see
 * the last computed-at / last error per pool. The "Calculate All" button on
 * Calculation Control only succeeds once every pool is Ready; pools that
 * stay un-computed mean the calculate falls back to read-time standards.
 */
@Component({
  selector: 'app-pool-control',
  templateUrl: './pool-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoolControlComponent {
  private readonly proxy = inject(CostingPoolsProxyService);
  private readonly exchangeRateProxy = inject(ExchangeRateProxyService);
  private readonly monthlyCostProxy = inject(MonthlyCostProxyService);
  private readonly translate = inject(TranslateService);

  readonly year = input.required<number>();
  readonly month = input.required<number>();

  readonly poolsCompleted = output<PoolsControlCompleted>();
  /** Emitted after a successful monthly close so the parent can refresh the dashboards. */
  readonly calculated = output<void>();

  readonly exchangeRateControl = new FormControl<number | null>(null, [Validators.required, Validators.min(0.0001)]);
  readonly exchangeRateEurUsdControl = new FormControl<number>(0, [Validators.required, Validators.min(0)]);
  readonly exchangeRateLoading = signal<boolean>(false);
  readonly exchangeRateEffectiveAt = signal<string | null>(null);

  // ── Local state ────────────────────────────────────────────────────────────
  readonly collapsed = signal<boolean>(true);
  readonly loadingStatus = signal<boolean>(false);
  /** True while at least one /compute call is in flight. */
  readonly running = signal<boolean>(false);
  /** True while the monthly close (/calculate) is in flight. */
  readonly runningMonthlyClose = signal<boolean>(false);
  readonly rows = signal<PoolRow[]>(this.buildInitialRows());
  readonly lastCheckedAt = signal<Date | null>(null);

  readonly allReady = computed(() => this.rows().every((r) => r.status === PoolStatus.Ready));
  readonly canCompute = computed(() => !this.running() && this.exchangeRateControl.valid && this.exchangeRateEurUsdControl.valid);

  constructor() {
    effect(
      () => {
        this.year();
        this.month();
        untracked(() => {
          this.rows.set(this.buildInitialRows());
          this.lastCheckedAt.set(null);
          this.loadStatus();
          this.loadHnlRate();
        });
      },
      { allowSignalWrites: true },
    );
  }

  private loadHnlRate(): void {
    this.exchangeRateLoading.set(true);
    const today = new Date();
    const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    this.exchangeRateProxy
      .searchExchangeRate({ date, currency: Currency.HNL })
      .pipe(catchError(() => of({ success: false, data: null, errorMessage: null })))
      .subscribe((resp) => {
        const rate = resp.success && resp.data && resp.data.length > 0 ? resp.data[0] : null;
        if (rate) {
          // HNL per 1 USD (~24.70) both on screen and in the command: MonthlyCostController falls back
          // to this typed value interchangeably with the stored rate, so the two must be the same
          // orientation AND the same side of the spread — both are the average since #5221. No
          // conversion in either direction.
          this.exchangeRateControl.setValue(rate.averageRateToUSD ?? rate.rateToUSD);
          this.exchangeRateEffectiveAt.set(rate.effectiveDate);
        } else {
          this.exchangeRateControl.setValue(null);
          this.exchangeRateEffectiveAt.set(null);
        }
        this.exchangeRateLoading.set(false);
      });
  }

  toggleCollapse(): void {
    this.collapsed.update((c) => !c);
  }

  // ── Status load ────────────────────────────────────────────────────────────
  loadStatus(): void {
    const y = this.year();
    const m = this.month();
    if (this.loadingStatus()) {
      return;
    }
    this.loadingStatus.set(true);

    this.proxy
      .getStatus(y, m)
      .pipe(catchError(() => of({ success: false, data: null, errorMessage: null })))
      .subscribe((resp) => {
        if (resp.success && resp.data) {
          this.rows.set(resp.data.pools.map((p) => this.toRow(p)));
        }
        this.lastCheckedAt.set(new Date());
        this.loadingStatus.set(false);
        this.emitCompleted();
      });
  }

  // ── Compute (single pool) ──────────────────────────────────────────────────
  computePool(poolName: PoolName): void {
    if (this.running()) {
      return;
    }
    // Read FormControl validity directly at click time. `canCompute` is a
    // `computed()` signal, but FormControls don't emit signal changes — so
    // canCompute would stay stuck at its initial state and gate the click
    // even after the user typed a valid rate.
    if (!this.exchangeRateControl.valid || !this.exchangeRateEurUsdControl.valid) {
      this.exchangeRateControl.markAsTouched();
      this.exchangeRateEurUsdControl.markAsTouched();
      return;
    }
    const cmd = {
      year: this.year(),
      month: this.month(),
      exchangeRate: this.exchangeRateControl.value ?? 0,
      exchangeRateEurUsd: this.exchangeRateEurUsdControl.value ?? 0,
    };

    this.markRow(poolName, { status: 'requesting', lastError: undefined });
    this.running.set(true);

    this.proxy
      .compute(poolName, cmd)
      .pipe(
        catchError((err) => {
          const message = getLocalizedValue(this.translate, err?.error?.errorMessage) || err?.message || 'Network error';
          this.markRow(poolName, { status: PoolStatus.Failed, lastError: message });
          this.running.set(false);
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response === null) {
          return;
        }
        if (response.success && response.data) {
          this.markRow(poolName, {
            status: response.data.status,
            computedAt: response.data.computedAt,
            computedBy: response.data.computedBy,
            lastError: response.data.lastError,
          });
          if (response.data.status === PoolStatus.Ready) {
            GlobalMessageUtil.success('Pool Control', `${poolName} pool computed.`);
          } else if (response.data.status === PoolStatus.Failed) {
            GlobalMessageUtil.warn('Pool Control', `${poolName} pool finished with errors.`);
          }
        }
        this.running.set(false);
        this.emitCompleted();
      });
  }

  // ── Run monthly close (POST /calculate) ────────────────────────────────────
  /**
   * Runs the full monthly close once every pool is Ready. The close reads the
   * pre-computed pools above instead of fanning out to upstream APIs.
   */
  runMonthlyClose(): void {
    if (this.running() || this.runningMonthlyClose()) {
      return;
    }
    if (!this.exchangeRateControl.valid || !this.exchangeRateEurUsdControl.valid) {
      this.exchangeRateControl.markAsTouched();
      this.exchangeRateEurUsdControl.markAsTouched();
      return;
    }
    const cmd: CalculateMonthlyCostCommand = {
      year: this.year(),
      month: this.month(),
      exchangeRate: this.exchangeRateControl.value ?? 0,
      exchangeRateEurUsd: this.exchangeRateEurUsdControl.value ?? 0,
      costCenterCodes: null,
    };

    this.runningMonthlyClose.set(true);
    this.monthlyCostProxy
      .calculate(cmd)
      .pipe(
        catchError(() => {
          this.runningMonthlyClose.set(false);
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response === null) {
          return;
        }
        const summaries = response.success && response.data ? response.data : [];
        const anySuccess = summaries.some((s) => s.status === MonthlyCostRunStatus.Success);
        const anyFailed = summaries.some((s) => s.status === MonthlyCostRunStatus.Failed);
        if (anySuccess && !anyFailed) {
          GlobalMessageUtil.success('Cost Analysis', this.translate.instant('Cost_Analysis_Calc_Complete'));
        } else if (anySuccess && anyFailed) {
          GlobalMessageUtil.warn('Cost Analysis', 'Some areas failed. Review the results.');
        } else if (anyFailed) {
          GlobalMessageUtil.error('Cost Analysis', this.translate.instant('Cost_Analysis_Calc_Failed'));
        }
        this.runningMonthlyClose.set(false);
        if (anySuccess) {
          this.calculated.emit();
        }
      });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  chipClasses(status: PoolRow['status']): string {
    const base = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium';
    switch (status) {
      case 'not-computed':
        return `${base} bg-[var(--p-surface-100)] text-[var(--p-surface-700)]`;
      case PoolStatus.Pending:
        return `${base} bg-[var(--p-surface-100)] text-[var(--p-surface-700)]`;
      case PoolStatus.Computing:
      case 'requesting':
        return `${base} bg-[var(--p-orange-100)] text-[var(--p-orange-700)]`;
      case PoolStatus.Ready:
        return `${base} bg-[var(--p-green-200)] text-[var(--p-green-800)]`;
      case PoolStatus.Failed:
        return `${base} bg-[var(--p-red-200)] text-[var(--p-red-800)]`;
      default:
        return base;
    }
  }

  chipIcon(status: PoolRow['status']): string {
    switch (status) {
      case 'not-computed':
      case PoolStatus.Pending:
        return 'pi pi-circle';
      case PoolStatus.Computing:
      case 'requesting':
        return 'pi pi-spin pi-spinner';
      case PoolStatus.Ready:
        return 'pi pi-check-circle';
      case PoolStatus.Failed:
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-circle';
    }
  }

  chipLabel(status: PoolRow['status']): string {
    switch (status) {
      case 'not-computed':
        return 'Not computed';
      case PoolStatus.Pending:
        return 'Pending';
      case PoolStatus.Computing:
        return 'Computing';
      case 'requesting':
        return 'Requesting';
      case PoolStatus.Ready:
        return 'Ready';
      case PoolStatus.Failed:
        return 'Failed';
      default:
        return 'Unknown';
    }
  }

  trackByName(_index: number, row: PoolRow): string {
    return row.name;
  }

  private buildInitialRows(): PoolRow[] {
    return ALL_POOL_NAMES.map((name) => ({ name, status: 'not-computed' as const }));
  }

  private toRow(entry: PoolStatusEntry): PoolRow {
    return {
      name: entry.name,
      status: entry.status ?? 'not-computed',
      computedAt: entry.computedAt,
      computedBy: entry.computedBy,
      lastError: entry.lastError,
    };
  }

  private markRow(poolName: PoolName, patch: Partial<PoolRow>): void {
    this.rows.update((rs) => rs.map((r) => (r.name === poolName ? { ...r, ...patch } : r)));
  }

  private emitCompleted(): void {
    const rs = this.rows();
    const anyReady = rs.some((r) => r.status === PoolStatus.Ready);
    const allReady = rs.every((r) => r.status === PoolStatus.Ready);
    this.poolsCompleted.emit({ anyReady, allReady });
  }
}
